/**
 * OurTeamPage · v2.7.65+E239
 * ==========================
 * Public /our-team page (replaces the old /leadership + standalone /drivers
 * pages). Fully CMS-managed via /site/our-team.
 *
 * Renders four fixed sections in order — Leadership · Dispatch · Drivers ·
 * Specialty Support — each heading ABOVE its own profiles, displayed three
 * across and centered. Each profile: photo, name, position title, short bio,
 * phone, email. The editable Buller Taxi dispatch phone + email show at the
 * very bottom.
 *
 * Theme-immune dark styling (inline colors + `.ps-heading`, see App.css) so
 * it stays readable in both light & dark site themes.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import api from "@/lib/api";
import { Loader2, Phone, Mail, Users } from "lucide-react";

export default function OurTeamPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/site/our-team")
      .then((r) => setData(r.data || {}))
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <MarketingLayout>
        <div className="py-24 text-center"><Loader2 className="h-7 w-7 animate-spin mx-auto text-zinc-500" /></div>
      </MarketingLayout>
    );
  }

  const sections = (data?.sections || []).map((s) => ({
    ...s,
    profiles: (s.profiles || []).filter((p) => p.enabled !== false),
  }));
  const visibleSections = sections.filter((s) => s.profiles.length > 0);
  const dispatch = data?.dispatch || {};
  const phone = (dispatch.phone || "").trim();
  const email = (dispatch.email || "").trim();

  return (
    <MarketingLayout>
      <section className="relative" style={{ backgroundColor: "#0a0a0b" }} data-testid="our-team-page">
        {/* Hero */}
        <div className="border-b" style={{ borderColor: "rgba(251,191,36,0.35)" }}>
          <div className="max-w-6xl mx-auto px-5 py-14 sm:py-20 text-center">
            <div className="text-[10px] uppercase tracking-[0.4em] font-bold" style={{ color: "#fbbf24" }}>Buller Taxi Service</div>
            <h1 className="ps-heading text-4xl sm:text-5xl font-black mt-2" style={{ color: "#f5f5f5" }} data-testid="our-team-title">Our Team</h1>
            <p className="mt-3 max-w-2xl mx-auto leading-relaxed text-base" style={{ color: "#d4d4d8" }} data-testid="our-team-intro">
              The people who keep Weatherford moving — leadership, dispatch, drivers and specialty support.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 py-12 sm:py-16 space-y-16">
          {visibleSections.length === 0 ? (
            <div className="text-center py-16" data-testid="our-team-empty">
              <Users className="h-10 w-10 mx-auto mb-3" style={{ color: "rgba(251,191,36,0.5)" }} />
              <p style={{ color: "#a1a1aa" }}>Team profiles coming soon.</p>
            </div>
          ) : (
            visibleSections.map((section) => (
              <div key={section.key} data-testid={`our-team-section-${section.key}`}>
                {section.image_url ? (
                  <div className="relative rounded-2xl overflow-hidden mb-8 h-44 sm:h-56" data-testid={`our-team-banner-${section.key}`}>
                    <img
                      src={section.image_url}
                      alt={section.heading}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(rgba(10,10,11,0.45), rgba(10,10,11,0.78))" }} aria-hidden="true" />
                    <div className="relative h-full flex flex-col items-center justify-center text-center px-5">
                      <h2 className="ps-heading text-2xl sm:text-3xl font-black uppercase tracking-wide" style={{ color: "#f5f5f5" }} data-testid={`our-team-heading-${section.key}`}>{section.heading}</h2>
                      <span className="block h-1 w-16 mx-auto mt-3 rounded-full" style={{ backgroundColor: "#fbbf24" }} />
                      {section.blurb && <p className="mt-3 max-w-2xl text-sm sm:text-base leading-relaxed" style={{ color: "#e4e4e7" }} data-testid={`our-team-blurb-${section.key}`}>{section.blurb}</p>}
                    </div>
                  </div>
                ) : (
                  <div className="text-center mb-8">
                    <h2 className="ps-heading text-2xl sm:text-3xl font-black uppercase tracking-wide inline-block relative" style={{ color: "#f5f5f5" }} data-testid={`our-team-heading-${section.key}`}>
                      {section.heading}
                      <span className="block h-1 w-16 mx-auto mt-3 rounded-full" style={{ backgroundColor: "#fbbf24" }} />
                    </h2>
                    {section.blurb && <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed" style={{ color: "#d4d4d8" }} data-testid={`our-team-blurb-${section.key}`}>{section.blurb}</p>}
                  </div>
                )}
                <div className="flex flex-wrap justify-center items-stretch gap-8" data-testid={`our-team-grid-${section.key}`}>
                  {section.profiles.map((p) => (
                    <ProfileCard key={p.id} profile={p} sectionKey={section.key} />
                  ))}
                </div>
              </div>
            ))
          )}

          {/* Dispatch contact — bottom of page */}
          {(phone || email) && (
            <div className="pt-6 text-center" data-testid="our-team-dispatch">
              <div className="inline-block rounded-2xl border px-8 py-6" style={{ borderColor: "rgba(251,191,36,0.35)", backgroundColor: "#141416" }}>
                <div className="text-[10px] uppercase tracking-[0.35em] font-bold mb-3" style={{ color: "#fbbf24" }}>Buller Taxi Dispatch</div>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
                  {phone && (
                    <a href={`tel:${phone.replace(/\D/g, "")}`} className="inline-flex items-center gap-2 font-bold hover:opacity-80 transition-opacity" style={{ color: "#f5f5f5" }} data-testid="our-team-dispatch-phone">
                      <Phone className="h-5 w-5" style={{ color: "#fbbf24" }} />{phone}
                    </a>
                  )}
                  {email && (
                    <a href={`mailto:${email}`} className="inline-flex items-center gap-2 font-bold hover:opacity-80 transition-opacity" style={{ color: "#f5f5f5" }} data-testid="our-team-dispatch-email">
                      <Mail className="h-5 w-5" style={{ color: "#fbbf24" }} />{email}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}

function ProfileCard({ profile: p, sectionKey }) {
  const [expanded, setExpanded] = useState(false);
  const [fullH, setFullH] = useState(0);
  const [overflowing, setOverflowing] = useState(false);
  const bioRef = useRef(null);
  const name = (p.name || "").trim();
  const phone = (p.phone || "").trim();
  const email = (p.email || "").trim();
  // Bio is hard-capped at 150 words. The card clamps it to ~5 lines and a
  // "Read more" toggle animates the full text open in place.
  const words = (p.bio || "").trim().length ? (p.bio || "").trim().split(/\s+/) : [];
  const fullBio = words.slice(0, 150).join(" ");
  const COLLAPSED = 112; // px ≈ 5 lines of text-sm / leading-relaxed

  useLayoutEffect(() => {
    const el = bioRef.current;
    if (!el) return undefined;
    const measure = () => { const h = el.scrollHeight; setFullH(h); setOverflowing(h > COLLAPSED + 6); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [fullBio]);

  const bioMaxHeight = !overflowing ? "none" : expanded ? `${fullH}px` : `${COLLAPSED}px`;

  return (
    <div
      className="group w-[320px] sm:w-[360px] lg:w-[380px] rounded-2xl overflow-hidden border flex flex-col transition-transform duration-300 ease-out hover:-translate-y-1"
      style={{ borderColor: "rgba(251,191,36,0.25)", backgroundColor: "#141416" }}
      data-testid={`our-team-profile-${sectionKey}-${p.id}`}
    >
      <div className="w-full h-64 overflow-hidden" style={{ backgroundColor: "#0a0a0b" }}>
        {p.image_url ? (
          <img
            src={p.image_url}
            alt={name || p.title || "Team member"}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            data-testid={`our-team-profile-${sectionKey}-${p.id}-image`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Users className="h-12 w-12" style={{ color: "rgba(251,191,36,0.35)" }} />
          </div>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col text-center">
        {name && <h3 className="text-xl font-black uppercase tracking-wide leading-tight" style={{ color: "#f5f5f5" }} data-testid={`our-team-profile-${sectionKey}-${p.id}-name`}>{name}</h3>}
        {p.title && <div className="text-xs font-bold uppercase tracking-wider mt-1" style={{ color: "#fbbf24" }} data-testid={`our-team-profile-${sectionKey}-${p.id}-title`}>{p.title}</div>}
        {fullBio.length > 0 && (
          <div className="mt-3">
            <div
              className="overflow-hidden"
              style={{ maxHeight: bioMaxHeight, transition: "max-height 500ms ease-in-out" }}
            >
              <p ref={bioRef} className="text-sm leading-relaxed" style={{ color: "#d4d4d8" }} data-testid={`our-team-profile-${sectionKey}-${p.id}-bio`}>{fullBio}</p>
            </div>
            {overflowing && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-2 text-xs font-bold uppercase tracking-wider hover:opacity-80 transition-opacity"
                style={{ color: "#fbbf24" }}
                data-testid={`our-team-profile-${sectionKey}-${p.id}-toggle`}
              >
                {expanded ? "Read less" : "Read more"}
              </button>
            )}
          </div>
        )}
        {(phone || email) && (
          <div className="mt-auto pt-4 border-t flex flex-col items-center gap-2 text-sm" style={{ borderColor: "rgba(255,255,255,0.08)", color: "#d4d4d8" }}>
            {phone && (
              <a href={`tel:${phone.replace(/\D/g, "")}`} className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid={`our-team-profile-${sectionKey}-${p.id}-phone`}>
                <Phone className="h-4 w-4" style={{ color: "#fbbf24" }} />{phone}
              </a>
            )}
            {email && (
              <a href={`mailto:${email}`} className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity break-all" data-testid={`our-team-profile-${sectionKey}-${p.id}-email`}>
                <Mail className="h-4 w-4 shrink-0" style={{ color: "#fbbf24" }} />{email}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
